import { Check } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { NeonButton, GhostButton } from "@/components/Buttons";
import { checkoutToast } from "@/lib/ui";

const INCLUDES = [
  "P.E.E. OS access",
  "Execution Traders Community",
  "Executor Training",
  "P.E.E. Analytics",
  "Alerts",
  "Community resources",
  "Training resources",
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          chapter="11 — Access"
          align="center"
          testid="pricing-heading"
          title={
            <>
              One system.
              <br />
              Two ways <span className="glow-text-neon text-neon">in.</span>
            </>
          }
        />

        <div className="mx-auto mt-16 grid max-w-4xl gap-6 lg:grid-cols-2 lg:items-stretch">
          <Reveal delay={0.1} className="order-2 h-full lg:order-1">
            <div
              data-testid="pricing-monthly-card"
              className="flex h-full flex-col rounded-xl border border-line bg-charcoal p-8 sm:p-10"
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-mist">
                Monthly
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-6xl font-extrabold tracking-tight text-white">
                  $147
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-dim">
                  / month
                </span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-mist">
                Flexible monthly access to the P.E.E. ecosystem.
              </p>
              <ul className="mt-8 space-y-3.5">
                {INCLUDES.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-mist">
                    <Check size={15} className="shrink-0 text-neon" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-10">
                <GhostButton
                  testid="pricing-monthly-cta"
                  onClick={() => checkoutToast("Monthly")}
                  className="w-full"
                >
                  Start Monthly
                </GhostButton>
                <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-dim">
                  Cancel according to membership terms.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="order-1 h-full lg:order-2">
            <div
              data-testid="pricing-annual-card"
              className="glow-neon relative flex h-full flex-col rounded-xl border border-neon/60 bg-charcoal p-8 sm:p-10"
            >
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-neon px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink">
                Best Value
              </span>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-neon">
                Annual
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-6xl font-extrabold tracking-tight text-white">
                  $997
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-dim">
                  / year
                </span>
              </div>
              <div className="mt-4 space-y-1.5">
                <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-dim line-through">
                  $1,764 value when paid monthly
                </p>
                <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-neon">
                  Save $767
                </p>
                <p className="text-sm text-mist">
                  Equivalent to approximately $83/month · 12 months of access.
                </p>
              </div>
              <ul className="mt-8 space-y-3.5">
                <li className="flex items-center gap-3 text-sm font-medium text-white">
                  <Check size={15} className="shrink-0 text-neon" />
                  Everything included in the monthly membership
                </li>
                {INCLUDES.slice(0, 4).map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-mist">
                    <Check size={15} className="shrink-0 text-neon" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-10">
                <NeonButton
                  testid="pricing-annual-cta"
                  onClick={() => checkoutToast("Annual")}
                  className="w-full"
                >
                  Get Annual Access
                </NeonButton>
                <p className="mt-4 text-center font-mono text-[10px] leading-relaxed tracking-[0.1em] text-dim">
                  Flexible payment options may be available at checkout for
                  eligible customers.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
